import { mergeClasses, Tooltip } from "@fluentui/react-components";
import { PinIcon } from "apps/project-iq/components/icons/ProjectIqIcons";
import { projectIqTokens } from "apps/project-iq/theme/projectIqTokens";
import { useMemo, useState } from "react";
import { useWorkspaceCardStyles } from "./WorkspaceCard.styles";
import { IWorkspaceCardProps } from "./WorkspaceCard.types";

const AVATAR_COLORS = [
  projectIqTokens.teal2,
  projectIqTokens.violet,
  projectIqTokens.green,
  projectIqTokens.amber,
  projectIqTokens.rust,
];

const MAX_AVATARS = 3;

const getInitials = (name: string): string => {
  if (!name.trim()) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

export const WorkspaceCard = ({
  workspace,
  onClick,
  isPinned = false,
  onTogglePin,
}: IWorkspaceCardProps): JSX.Element => {
  const styles = useWorkspaceCardStyles();
  const [isHovered, setIsHovered] = useState(false);

  const allMembers = useMemo(() => {
    const seen = new Map<string, (typeof workspace.owners)[0]>();
    [...(workspace.owners ?? []), ...(workspace.teamMembers ?? [])].forEach(
      (member) => {
        if (!seen.has(member.userId)) seen.set(member.userId, member);
      },
    );
    return [...seen.values()];
  }, [workspace.owners, workspace.teamMembers]);

  return (
    <div
      className={styles.card}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      aria-label={
        workspace.clientName
          ? `${workspace.name} — ${workspace.clientName}`
          : workspace.name
      }
    >
      <div className={styles.cardTop}>
        <div className={styles.clientRow}>
          <span className={styles.clientName}>{workspace.clientName}</span>
        </div>
        {onTogglePin && (
          <button
            className={mergeClasses(
              styles.pinBtn,
              (isHovered || isPinned) && styles.pinBtnVisible,
            )}
            style={{
              background: isPinned
                ? projectIqTokens.tealSoft
                : projectIqTokens.card,
              border: `1px solid ${isPinned ? projectIqTokens.teal : projectIqTokens.line}`,
              color: isPinned ? projectIqTokens.teal : projectIqTokens.faint,
            }}
            onClick={(e) => {
              e.stopPropagation();
              onTogglePin(e);
            }}
            title={isPinned ? "Unpin from sidebar" : "Pin to sidebar"}
            aria-label={isPinned ? "Unpin from sidebar" : "Pin to sidebar"}
          >
            <PinIcon filled={isPinned} />
          </button>
        )}
      </div>

      <div className={styles.nameBlock}>
        <Tooltip
          content={workspace.name}
          relationship="label"
          positioning="above"
        >
          <div className={styles.workspaceName}>{workspace.name}</div>
        </Tooltip>
        {workspace.code && (
          <div className={styles.codeLine}>
            <span>{workspace.code}</span>
          </div>
        )}
      </div>

      <div className={styles.cardFooter}>
        <div className={styles.avatarRow}>
          {allMembers.slice(0, MAX_AVATARS).map((m, i) => (
            <div
              key={m.userId}
              style={{
                marginLeft: i > 0 ? "-8px" : "0",
                boxShadow: projectIqTokens.avatarRingShadow,
                borderRadius: "999px",
                flexShrink: 0,
              }}
              title={m.displayName}
            >
              <div
                className={styles.avatar}
                style={{ background: AVATAR_COLORS[i % AVATAR_COLORS.length] }}
              >
                {getInitials(m.displayName)}
              </div>
            </div>
          ))}
          {allMembers.length > MAX_AVATARS && (
            <Tooltip
              content={allMembers.map((m) => m.displayName).join(", ")}
              relationship="label"
              positioning="above"
            >
              <div
                role="button"
                tabIndex={0}
                style={{
                  marginLeft: "-8px",
                  boxShadow: projectIqTokens.avatarRingShadow,
                  borderRadius: "999px",
                  flexShrink: 0,
                }}
              >
                <div className={styles.overflowAvatar}>
                  +{allMembers.length - MAX_AVATARS}
                </div>
              </div>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
};
